import { AfterViewInit, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Container } from '../container';

@Component({
  selector: 'screen-group-cube',
  templateUrl: './cube.component.html',
  styleUrls: ['./cube.component.less']
})
export class CubeRotateComponent extends Container implements AfterViewInit, OnDestroy {
  private _cube: HTMLElement;
  private _front: HTMLElement;
  private _back: HTMLElement;
  private _left: HTMLElement;
  private _right: HTMLElement;
  private _top: HTMLElement;
  private _bottom: HTMLElement;

  private _initialized = false;
  private _size = 200;

  private _behavior = new BehaviorSubject(null);

  private _autoplay = true;
  private _interval = 6000;
  private _backfaceInvisible = false;
  private _intervalHandle: any = 0;

  private _array = ['front', 'left', 'back', 'right', 'top', 'bottom'];

  @Input('face')
  set face(param: string) {
    this._behavior.next(param);
  }

  @Input()
  set autoplay(param: boolean) {
    this._autoplay = !!param;
    if (this._initialized) {
      this._hackAutoplay();
    }
  }

  @Input()
  set interval(param: number) {
    if (param !== this._interval && param > 1000) {
      this._interval = param;
      if (this._initialized) {
        this._hackAutoplay();
      }
    }
  }

  @Input()
  set backfaceInvisible(param: boolean) {
    this._backfaceInvisible = param;
  }

  get backfaceInvisible(): boolean {
    return this._backfaceInvisible;
  }

  constructor(private _elementRef: ElementRef) {
    super();
  }

  ngAfterViewInit() {
    const ele = this._elementRef.nativeElement as HTMLElement;
    this._cube = ele.querySelector('.cube'),
      this._front = ele.querySelector('.front'),
      this._back = ele.querySelector('.back'),
      this._right = ele.querySelector('.right'),
      this._left = ele.querySelector('.left'),
      this._top = ele.querySelector('.top'),
      this._bottom = ele.querySelector('.bottom');

    this._init();
    this.addSubscription(() => {
      this.stop();
    });
    this.addSubscription(this._behavior.subscribe((value) => {
      this._show(value);
    }));
  }

  start() {
    this.stop();
    let count = 0;
    this._intervalHandle = setInterval(() => {
      this._show(this._array[(count++) % 6]);
    }, this._interval);
  }

  stop() {
    if (this._intervalHandle) {
      clearInterval(this._intervalHandle);
      this._intervalHandle = 0;
    }
  }

  resize() {
    this._init();
  }

  /**
   * .cube transform: translateZ( -200px );
   * &.front  { transform: rotateY(   0deg ) translateZ( 200px ); }
   * &.back   { transform: rotateX( 180deg ) translateZ( 200px ); }
   * &.right  { transform: rotateY(  90deg ) translateZ( 200px ); }
   * &.left   { transform: rotateY( -90deg ) translateZ( 200px ); }
   * &.top    { transform: rotateX(  90deg ) translateZ( 200px ); }
   * &.bottom { transform: rotateX( -90deg ) translateZ( 200px ); }
   * @param width
   * @private
   */
  private _init() {
    const ele = this._elementRef.nativeElement as HTMLElement, parent = ele.parentElement;
    const size = Math.min(parent.clientWidth, parent.clientHeight);
    if (size > 100) {
      this._size = size;
    }
    ele.style.width = `${this._size}px`;
    ele.style.height = `${this._size}px`;

    const offset = this._size / 2;

    this._cube.style.transform = `translateZ( -${offset}px )`;
    this._front.style.transform = `rotateY( 0deg ) translateZ( ${offset}px )`;
    this._back.style.transform = `rotateX( 180deg ) translateZ( ${offset}px )`;
    this._right.style.transform = `rotateY( 90deg ) translateZ( ${offset}px )`;
    this._left.style.transform = `rotateY( -90deg ) translateZ( ${offset}px )`;
    this._top.style.transform = `rotateX( 90deg ) translateZ( ${offset}px )`;
    this._bottom.style.transform = `rotateX( -90deg ) translateZ( ${offset}px )`;
    this._hackAutoplay();
  }

  private _hackAutoplay() {
    if (this._autoplay) {
      this.start();
    }
  }

  /**
   * &.show-front  { transform: translateZ( -200px ) rotateY(    0deg ); }
   * &.show-back   { transform: translateZ( -200px ) rotateX( -180deg ); }
   * &.show-right  { transform: translateZ( -200px ) rotateY(  -90deg ); }
   * &.show-left   { transform: translateZ( -200px ) rotateY(   90deg ); }
   * &.show-top    { transform: translateZ( -200px ) rotateX(  -90deg ); }
   * &.show-bottom { transform: translateZ( -200px ) rotateX(   90deg ); }
   * @param face
   * @private
   */
  private _show(face: string) {
    const cube = this._cube;
    switch (face) {
      case 'front':
        cube.style.transform = `translateZ( -${this._size / 2}px ) rotateY( 0deg )`;
        break;
      case 'back':
        cube.style.transform = `translateZ( -${this._size / 2}px ) rotateX( -180deg )`;
        break;
      case 'right':
        cube.style.transform = `translateZ( -${this._size / 2}px ) rotateY( -90deg )`;
        break;
      case 'left':
        cube.style.transform = `translateZ( -${this._size / 2}px ) rotateY( 90deg )`;
        break;
      case 'top':
        cube.style.transform = `translateZ( -${this._size / 2}px ) rotateX( -90deg )`;
        break;
      case 'bottom':
        cube.style.transform = `translateZ( -${this._size / 2}px ) rotateX( 90deg )`;
        break;
      default:
        return;
    }
  }
}
